import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui flyHome components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// flyHome React components
import MDBox from "components/MDBox";

// flyHome React components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// flyHome React components
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// flyHome React components
import themeDark from "assets/theme-dark";
// import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
// import rtlPlugin from "stylis-plugin-rtl";
// import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// flyHome React components routes
//import routes from "routes";

// flyHome React components contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import SignIn from "layouts/authentication/sign-in";
import PoliceSignIn from "layouts/authentication/police/sign-in";
import PrivacyPolicyPage from "layouts/pages/privacyPolicyPage";
import AboutUsPage from "layouts/pages/aboutUsPage";
import SupportPage from "layouts/pages/supportPage";

import { adminRoutes, policeRoutes, authRoutes } from "routes";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const navigate = useNavigate();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    let user = localStorage.getItem("user");
    // Do not redirect if on /about-us
    if (
      !user &&
      pathname !== "/about-us" &&
      pathname !== "/privacy-policy" &&
      pathname !== "/support"
    ) {
      // Check if we're on the police path to decide which sign-in to show
      if (pathname.includes("/police")) {
        navigate("/police/sign-in");
      } else {
        navigate("/sign-in");
      }
    }
  }, [navigate, pathname]);

  // Setting the theme for the sidenav

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      //stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutesForRole = (role) => {
    switch (role) {
      case "Police":
        return policeRoutes;
      case "Admin":
      default:
        return adminRoutes;
    }
  };

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    // Check if the user is authenticated
    const userStr = localStorage.getItem("user");
    if (userStr) {
      setIsAuthenticated(true);

      // Get user role (if stored)
      try {
        const user = JSON.parse(userStr);
        // Check role from user object or session storage
        const role = sessionStorage.getItem("role") || "Admin";
        setUserRole(role);
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    } else {
      setIsAuthenticated(false);
      // If not authenticated, set the role based on current path
      if (pathname.includes("/police")) {
        setUserRole("Police");
      } else {
        setUserRole("Admin");
      }
    }
  }, [pathname]);

  // Filter out the '/sign-in' route from the sidenav when the user is authenticated
  // const filteredRoutes = isAuthenticated
  //   ? routes.filter((route) => route.path !== "/sign-in")
  //   : routes;

  const filteredRoutes = isAuthenticated
    ? getRoutesForRole(userRole).filter((route) => !route.route?.includes("sign-in"))
    : getRoutesForRole(userRole);

  console.log("isAuthenticated", isAuthenticated, localStorage.getItem("user"));

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {/* Hide sidebar and configurator on /about-us and /privacy-policy */}
      {layout === "dashboard" &&
        pathname !== "/about-us" &&
        pathname !== "/privacy-policy" &&
        pathname !== "/support" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="Fly Home Dashboard"
              routes={filteredRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
              role={userRole}
            />
            <Configurator />
            {configsButton}
          </>
        )}
      <Routes>
        {isAuthenticated ? (
          <Route path="/sign-in" element={<Navigate to="/dashboard" />} />
        ) : (
          <Route path="/sign-in" element={<SignIn />} />
        )}
        {isAuthenticated ? (
          <Route path="/police/sign-in" element={<Navigate to="/dashboard" />} />
        ) : (
          <Route path="/police/sign-in" element={<PoliceSignIn />} />
        )}
        {/* Public About Us route */}
        <Route path="/about-us" element={<AboutUsPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        {getRoutes(filteredRoutes)}
        <Route
          path="*"
          element={
            <Navigate
              to={
                isAuthenticated
                  ? userRole === "Police"
                    ? "/police-dashboard"
                    : "/dashboard"
                  : userRole === "Police"
                  ? "/police/sign-in"
                  : "/sign-in"
              }
            />
          }
        />
      </Routes>
    </ThemeProvider>
  );
}
